import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import AccessRequestList from '@devnw/cyber-iam/iam/AccessRequestList/AccessRequestList'
// eslint-disable-next-line import/no-unresolved
import AccessRequestSubmitForm from '@devnw/cyber-iam/iam/AccessRequestSubmitForm/AccessRequestSubmitForm'
import type { AccessRequestSubmission } from '@devnw/cyber-iam/iam/types'
import { useAccessRequestsApi } from '../api/accessRequestsClient'
import type { AccessRequest } from '../api/types'
import { toIamAccessRequest } from '../api/accessRequestMapping'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { useAuth } from '../auth/AuthContext'

function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const PageHeading = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

const SectionHeading = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.md};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

/**
 * OIDCPAM Gate-6 Phase 5a: migrated off MUI onto
 * @devnw/cyber-iam/iam/AccessRequestList + iam/AccessRequestSubmitForm — the
 * exact parity components built from this page. This page still owns every
 * API call (list/submit), the wire-mapping (snake_case <-> the library's
 * camelCase AccessRequest/AccessRequestSubmission shapes), tenant gating,
 * and routing to the detail page.
 */
export const AccessRequests = (): JSX.Element => {
  const navigate = useNavigate()
  const { tenant } = useActiveTenant()
  const { claims } = useAuth()
  const accessRequestsApi = useAccessRequestsApi()
  const [items, setItems] = useState<AccessRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = (): void => {
    if (!tenant || tenant === 'system') {
      setItems([])
      setLoading(false)
      return
    }
    setLoading(true)
    accessRequestsApi
      .list(tenant)
      .then((r) => setItems(r.items ?? []))
      .catch((e: unknown) => setError(errMessage(e)))
      .finally(() => setLoading(false))
  }
  useEffect(load, [tenant])

  // AccessRequestSubmitForm owns the form's own fields/validation/submitting/
  // success-note UI (AccessRequestSubmission composed there) — this page
  // owns only the wire mapping to the snake_case AccessRequestSubmitBody and
  // the API call + idempotency key, exactly as it did before migration.
  const handleSubmit = async (submission: AccessRequestSubmission): Promise<void> => {
    await accessRequestsApi.submit(
      tenant,
      {
        subject_id: claims?.sub ?? '',
        resource_id: submission.resourceId,
        actions: [],
        permissions: [],
        purpose: submission.purpose,
        justification: submission.justification,
        ticket_ref: {
          system: submission.ticketRef.system,
          external_id: submission.ticketRef.externalId,
        },
        requested_duration_seconds: submission.requestedDurationSeconds,
      },
      newIdempotencyKey()
    )
  }

  if (tenant === 'system') {
    return (
      <PageContainer>
        <MutedText>Please select a tenant to manage access requests.</MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeading>Access Requests</PageHeading>

      <Section>
        <SectionHeading>All Requests</SectionHeading>
        <AccessRequestList
          testID="access-requests-list"
          requests={items.map(toIamAccessRequest)}
          onSelect={(req) => navigate(`/admin/access-requests/${req.id}`)}
          loading={loading}
          error={error ?? undefined}
          emptyText="No requests found."
        />
      </Section>

      <AccessRequestSubmitForm
        testID="access-requests-submit"
        title="Submit New Request"
        onSubmit={handleSubmit}
        onSubmitted={() => load()}
      />
    </PageContainer>
  )
}
